import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { useTranslation } from "react-i18next";
import { FaTshirt, FaWhatsapp } from "react-icons/fa";
import { LuMinus, LuPencil, LuSparkles } from "react-icons/lu";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import type { TeamPlayerVM } from "@/components/v2/home/HomeConfirmedCard";
import type { LineupMember } from "@/lib/v2/lineup";
import { buildBalancedLineup, keysToMembers } from "@/lib/v2/tentativeLineup";
import { homeFieldInteractive, homeInputField } from "@/components/v2/home/homeFieldStyles";
import { HomeAdminZone } from "@/components/v2/home/HomeAdminZone";

export interface LineupPlayer {
  key: string;
  name: string;
  playerId?: number;
  guestRsvpId?: string;
  guestName?: string;
  balanceScore: number;
}

interface HomeLineupPanelProps {
  isAdmin: boolean;
  hasPublishedLineup: boolean;
  publishedTeamA: TeamPlayerVM[];
  publishedTeamB: TeamPlayerVM[];
  currentUserTeam: "A" | "B" | null;
  goingPlayers: LineupPlayer[];
  eventId: string;
  currentUserPlayerId?: number | null;
  publishingLineup?: boolean;
  onPublishLineup?: (teamA: LineupMember[], teamB: LineupMember[]) => Promise<void>;
  onShareWhatsApp?: () => void;
  /** When true, admin controls render without their own HomeAdminZone (for parent merge). */
  detachAdmin?: boolean;
  /** Show a subsection heading when admin block is embedded in a parent zone. */
  mergedAdminSection?: boolean;
}

interface HomeLineupPanelSlots {
  member: ReactNode;
  admin: ReactNode | null;
}

type HomeLineupPanelContextValue = HomeLineupPanelSlots & {
  hasPlayers: boolean;
  stickyPrimary?: {
    label: string;
    disabled: boolean;
    loading: boolean;
    onClick: () => void;
  } | null;
};

const HomeLineupPanelContext = createContext<HomeLineupPanelContextValue | null>(null);

export function useHomeLineupStickyPrimary() {
  return useContext(HomeLineupPanelContext)?.stickyPrimary ?? null;
}

function toLineupMember(player: LineupPlayer): LineupMember {
  return {
    key: player.key,
    name: player.name,
    playerId: player.playerId,
    guestRsvpId: player.guestRsvpId,
    guestName: player.guestName,
    balanceScore: player.balanceScore,
  };
}

const teamLightPanel = {
  shell: "border-zinc-200 bg-white shadow-sm",
  title: "text-zinc-900",
  jersey: "text-zinc-500",
  icon: "text-zinc-400",
  empty: "text-zinc-500",
  row: "text-zinc-800",
  rowBg: "bg-zinc-100",
  action: "text-zinc-500 hover:bg-zinc-100",
} as const;

const teamDarkPanel = {
  shell: "border-zinc-700 bg-zinc-900 shadow-sm",
  title: "text-zinc-50",
  jersey: "text-zinc-400",
  icon: "text-zinc-300",
  empty: "text-zinc-500",
  row: "text-zinc-100",
  rowBg: "bg-zinc-800",
  action: "text-zinc-400 hover:bg-zinc-700",
} as const;

function teamPanelStyles(isLight: boolean) {
  return isLight ? teamLightPanel : teamDarkPanel;
}

function TeamColumn({
  side,
  players,
  title,
  jerseyHint,
}: {
  side: "A" | "B";
  players: TeamPlayerVM[];
  title: string;
  jerseyHint: string;
}) {
  const isLight = side === "A";
  const styles = teamPanelStyles(isLight);

  return (
    <div className={cn("space-y-2 rounded-lg border p-3", styles.shell)}>
      <div className="text-center">
        <p className={cn("flex items-center justify-center gap-1.5 font-extrabold", styles.title)}>
          <FaTshirt className={styles.icon} />
          {title}
        </p>
        <p className={cn("text-[11px]", styles.jersey)}>{jerseyHint}</p>
      </div>
      <ul className="space-y-1">
        {players.length === 0 ? (
          <li className={cn("py-2 text-center text-xs", styles.empty)}>—</li>
        ) : (
          players.map((p) => (
            <li
              key={p.key}
              className={cn(
                "rounded px-2 py-1.5 text-sm",
                styles.row,
                p.isCurrentUser
                  ? "bg-soccer-primary/20 font-bold ring-2 ring-soccer-primary"
                  : styles.rowBg
              )}
            >
              {p.name}
            </li>
          ))
        )}
      </ul>
    </div>
  );
}

function toTeamVM(
  keys: string[],
  playersByKey: Map<string, LineupPlayer>,
  currentUserPlayerId: number | null | undefined,
  prefix: "a" | "b"
): TeamPlayerVM[] {
  return keys
    .map((key) => {
      const player = playersByKey.get(key);
      if (!player) return null;
      return {
        key: `${prefix}-${key}`,
        name: player.name,
        isCurrentUser: currentUserPlayerId != null && player.playerId === currentUserPlayerId,
      };
    })
    .filter((p): p is TeamPlayerVM => p != null);
}

function useHomeLineupPanelSlots({
  isAdmin,
  hasPublishedLineup,
  publishedTeamA,
  publishedTeamB,
  currentUserTeam,
  goingPlayers,
  eventId,
  currentUserPlayerId,
  publishingLineup = false,
  onPublishLineup,
  onShareWhatsApp,
  mergedAdminSection = false,
}: HomeLineupPanelProps): HomeLineupPanelContextValue {
  const { t } = useTranslation();
  const playersByKey = useMemo(
    () => new Map(goingPlayers.map((p) => [p.key, p])),
    [goingPlayers]
  );

  const [editing, setEditing] = useState(!hasPublishedLineup && isAdmin);
  const [teamAKeys, setTeamAKeys] = useState<string[]>([]);
  const [teamBKeys, setTeamBKeys] = useState<string[]>([]);

  const publishedTeamAKeys = publishedTeamA.map((p) => p.key.replace(/^a-/, "")).join(",");
  const publishedTeamBKeys = publishedTeamB.map((p) => p.key.replace(/^b-/, "")).join(",");

  useEffect(() => {
    if (hasPublishedLineup) {
      setEditing(false);
      setTeamAKeys(publishedTeamAKeys ? publishedTeamAKeys.split(",") : []);
      setTeamBKeys(publishedTeamBKeys ? publishedTeamBKeys.split(",") : []);
      return;
    }
    setEditing(isAdmin);
    setTeamAKeys([]);
    setTeamBKeys([]);
  }, [eventId, hasPublishedLineup, isAdmin]);

  useEffect(() => {
    if (!hasPublishedLineup) return;
    setTeamAKeys(publishedTeamAKeys ? publishedTeamAKeys.split(",") : []);
    setTeamBKeys(publishedTeamBKeys ? publishedTeamBKeys.split(",") : []);
  }, [hasPublishedLineup, publishedTeamAKeys, publishedTeamBKeys]);

  useEffect(() => {
    if (hasPublishedLineup) return;
    const validKeys = new Set(goingPlayers.map((p) => p.key));
    setTeamAKeys((prev) => prev.filter((k) => validKeys.has(k)));
    setTeamBKeys((prev) => prev.filter((k) => validKeys.has(k)));
  }, [goingPlayers, hasPublishedLineup]);

  const assigned = useMemo(() => new Set([...teamAKeys, ...teamBKeys]), [teamAKeys, teamBKeys]);
  const unassigned = goingPlayers.filter((p) => !assigned.has(p.key));

  const tentative = useMemo(() => {
    const members = goingPlayers.map(toLineupMember);
    return buildBalancedLineup(members, eventId);
  }, [goingPlayers, eventId]);

  const displayTeamA = hasPublishedLineup
    ? publishedTeamA
    : toTeamVM(tentative.teamA, playersByKey, currentUserPlayerId, "a");
  const displayTeamB = hasPublishedLineup
    ? publishedTeamB
    : toTeamVM(tentative.teamB, playersByKey, currentUserPlayerId, "b");

  const editorTeamA = toTeamVM(teamAKeys, playersByKey, currentUserPlayerId, "a");
  const editorTeamB = toTeamVM(teamBKeys, playersByKey, currentUserPlayerId, "b");

  const tentativeUserTeam: "A" | "B" | null =
    !hasPublishedLineup && currentUserPlayerId != null
      ? tentative.teamA.includes(`player-${currentUserPlayerId}`)
        ? "A"
        : tentative.teamB.includes(`player-${currentUserPlayerId}`)
          ? "B"
          : null
      : null;

  const showJerseyReminder = hasPublishedLineup ? currentUserTeam : tentativeUserTeam;

  const assignTo = (playerKey: string, team: "A" | "B") => {
    setTeamAKeys((prev) => prev.filter((k) => k !== playerKey));
    setTeamBKeys((prev) => prev.filter((k) => k !== playerKey));
    if (team === "A") setTeamAKeys((prev) => [...prev, playerKey]);
    else setTeamBKeys((prev) => [...prev, playerKey]);
  };

  const removeFromTeams = (playerKey: string) => {
    setTeamAKeys((prev) => prev.filter((k) => k !== playerKey));
    setTeamBKeys((prev) => prev.filter((k) => k !== playerKey));
  };

  const handleAutoBalance = () => {
    setTeamAKeys(tentative.teamA);
    setTeamBKeys(tentative.teamB);
  };

  const canPublish = teamAKeys.length > 0 && teamBKeys.length > 0;

  const handlePublish = async () => {
    if (!canPublish || !onPublishLineup) return;
    const members = goingPlayers.map(toLineupMember);
    const byKey = new Map(members.map((m) => [m.key, m]));
    await onPublishLineup(
      keysToMembers(teamAKeys, byKey),
      keysToMembers(teamBKeys, byKey)
    );
    setEditing(false);
  };

  if (goingPlayers.length === 0) {
    return {
      hasPlayers: false,
      member: (
        <p className="text-center text-sm text-muted-foreground">{t("v2.home.lineupNoPlayers")}</p>
      ),
      admin: null,
      stickyPrimary: null,
    };
  }

  const showMemberTeams = !isAdmin || !editing || hasPublishedLineup || !onPublishLineup;

  const adminEditor =
    isAdmin && editing && onPublishLineup ? (
      <div className="space-y-3">
        {unassigned.length > 0 && (
          <div className="space-y-2">
            <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              {t("v2.home.lineupUnassigned")}
            </p>
            <div className="flex flex-wrap gap-1.5">
              {unassigned.map((player) => (
                <div
                  key={player.key}
                  className={cn(
                    "flex items-center gap-1 rounded-full border px-2 py-1 text-xs",
                    homeFieldInteractive
                  )}
                >
                  <span className="max-w-[120px] truncate font-medium">{player.name}</span>
                  <button
                    type="button"
                    onClick={() => assignTo(player.key, "A")}
                    className="flex min-h-[44px] min-w-[44px] items-center justify-center rounded-full bg-zinc-200/80 text-xs font-bold uppercase text-zinc-800 hover:bg-zinc-300 dark:bg-zinc-700 dark:text-zinc-100"
                  >
                    A
                  </button>
                  <button
                    type="button"
                    onClick={() => assignTo(player.key, "B")}
                    className="flex min-h-[44px] min-w-[44px] items-center justify-center rounded-full bg-zinc-800/80 text-xs font-bold uppercase text-zinc-100 hover:bg-zinc-700"
                  >
                    B
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="grid grid-cols-2 gap-3 text-sm">
          {(
            [
              ["A", editorTeamA, teamAKeys],
              ["B", editorTeamB, teamBKeys],
            ] as const
          ).map(([side, players, keys]) => {
            const isLight = side === "A";
            const styles = teamPanelStyles(isLight);
            return (
            <div key={side} className={cn("space-y-2 rounded-lg border p-3", styles.shell)}>
              <div className="text-center">
                <p
                  className={cn(
                    "flex items-center justify-center gap-1.5 text-sm font-extrabold",
                    styles.title
                  )}
                >
                  <FaTshirt className={styles.icon} aria-hidden />
                  {isLight ? t("v2.home.teamA") : t("v2.home.teamB")}
                </p>
                <p className={cn("text-[11px] font-medium", styles.jersey)}>
                  {isLight ? t("v2.home.teamAJersey") : t("v2.home.teamBJersey")}
                </p>
              </div>
              <ul className="space-y-1">
                {players.map((p) => {
                  const key = p.key.replace(/^[ab]-/, "");
                  return (
                    <li
                      key={p.key}
                      className={cn(
                        "flex items-center justify-between gap-1 rounded px-2 py-1.5 text-sm",
                        styles.rowBg,
                        styles.row
                      )}
                    >
                      <span className="truncate font-medium">{p.name}</span>
                      <div className="flex shrink-0 gap-0.5">
                        {side === "A" ? (
                          <button
                            type="button"
                            onClick={() => assignTo(key, "B")}
                            className={cn(
                              "flex min-h-[44px] min-w-[44px] items-center justify-center rounded-md text-xs font-bold",
                              styles.action
                            )}
                            title={t("v2.home.moveToTeamB")}
                          >
                            B
                          </button>
                        ) : (
                          <button
                            type="button"
                            onClick={() => assignTo(key, "A")}
                            className={cn(
                              "flex min-h-[44px] min-w-[44px] items-center justify-center rounded-md text-xs font-bold",
                              styles.action
                            )}
                            title={t("v2.home.moveToTeamA")}
                          >
                            A
                          </button>
                        )}
                        <button
                          type="button"
                          onClick={() => removeFromTeams(key)}
                          className={cn(
                            "flex min-h-[44px] min-w-[44px] items-center justify-center rounded-md hover:text-destructive",
                            styles.action
                          )}
                          aria-label={t("common.delete")}
                        >
                          <LuMinus className="h-4 w-4" />
                        </button>
                      </div>
                    </li>
                  );
                })}
                {keys.length === 0 && (
                  <li className={cn("py-2 text-center text-xs", styles.empty)}>
                    {t("v2.home.lineupEmptyTeam")}
                  </li>
                )}
              </ul>
            </div>
            );
          })}
        </div>

        <div className="flex flex-col gap-2 sm:flex-row">
          <Button
            type="button"
            variant="outline"
            className={cn(homeInputField, "min-h-[44px] flex-1 gap-2 font-semibold hover:shadow-none")}
            onClick={handleAutoBalance}
          >
            <LuSparkles className="shrink-0" />
            {t("v2.home.autoBalance")}
          </Button>
          <Button
            type="button"
            className="min-h-[44px] flex-1 bg-amber-600 font-bold text-white hover:bg-amber-600/90 shadow-sm"
            disabled={!canPublish || publishingLineup}
            onClick={handlePublish}
          >
            {publishingLineup
              ? t("common.loading")
              : hasPublishedLineup
                ? t("v2.home.updateLineup")
                : t("v2.home.confirmLineup")}
          </Button>
        </div>
      </div>
    ) : null;

  const adminControls =
    isAdmin && (adminEditor || !hasPublishedLineup || onPublishLineup) ? (
      <div className="space-y-3">
        {mergedAdminSection && (
          <p className="text-xs font-bold uppercase tracking-wide text-amber-900/80 dark:text-amber-200/90">
            {t("v2.home.adminZoneLineupTitle")}
          </p>
        )}
        {adminEditor}
        {hasPublishedLineup && !editing && onPublishLineup && (
          <Button
            type="button"
            variant="outline"
            className="min-h-[44px] w-full gap-2 font-bold"
            onClick={() => setEditing(true)}
          >
            <LuPencil className="shrink-0" />
            {t("v2.home.editLineup")}
          </Button>
        )}
      </div>
    ) : null;

  const member = (
    <div className="space-y-4">
      {!hasPublishedLineup && !isAdmin && (
        <div className="rounded-xl border border-amber-400/35 bg-amber-400/10 px-4 py-3 text-center text-sm font-medium text-amber-900 dark:text-amber-200">
          {t("v2.home.lineupTentativeHint")}
        </div>
      )}

      {hasPublishedLineup && showJerseyReminder && (
        <div className="rounded-xl border border-soccer-primary/40 bg-background/95 px-4 py-3 text-center text-sm font-bold shadow-sm">
          {showJerseyReminder === "A"
            ? t("v2.home.jerseyReminderLight")
            : t("v2.home.jerseyReminderDark")}
        </div>
      )}

      {!hasPublishedLineup && !isAdmin && showJerseyReminder && (
        <div className="rounded-xl border border-border/60 bg-muted/30 px-4 py-3 text-center text-sm text-muted-foreground">
          {showJerseyReminder === "A"
            ? t("v2.home.tentativeJerseyLight")
            : t("v2.home.tentativeJerseyDark")}
        </div>
      )}

      {showMemberTeams && (
        <>
          <div className="grid grid-cols-2 gap-3 text-sm">
            <TeamColumn
              side="A"
              players={displayTeamA}
              title={t("v2.home.teamA")}
              jerseyHint={t("v2.home.teamAJersey")}
            />
            <TeamColumn
              side="B"
              players={displayTeamB}
              title={t("v2.home.teamB")}
              jerseyHint={t("v2.home.teamBJersey")}
            />
          </div>

          {onShareWhatsApp && hasPublishedLineup && (
            <Button
              onClick={onShareWhatsApp}
              className="min-h-[44px] w-full bg-soccer-primary font-bold text-black hover:bg-soccer-primary/90"
            >
              <FaWhatsapp className="mr-1.5" />
              {t("v2.home.shareWhatsApp")}
            </Button>
          )}
        </>
      )}
    </div>
  );

  const stickyPrimary =
    isAdmin && onPublishLineup
      ? editing || !hasPublishedLineup
        ? {
            label: hasPublishedLineup
              ? t("v2.home.updateLineup")
              : t("v2.home.confirmLineup"),
            disabled: !canPublish,
            loading: Boolean(publishingLineup),
            onClick: handlePublish,
          }
        : {
            label: t("v2.home.editLineup"),
            disabled: false,
            loading: false,
            onClick: () => setEditing(true),
          }
      : null;

  return {
    hasPlayers: true,
    member,
    admin: adminControls,
    stickyPrimary,
  };
}

/** Shares lineup state so member view and admin controls can render in different DOM slots. */
export function HomeLineupPanelRoot({
  children,
  ...props
}: HomeLineupPanelProps & { children: ReactNode }) {
  const slots = useHomeLineupPanelSlots(props);
  return (
    <HomeLineupPanelContext.Provider value={slots}>{children}</HomeLineupPanelContext.Provider>
  );
}

export function HomeLineupMemberView() {
  const ctx = useContext(HomeLineupPanelContext);
  if (!ctx) return null;
  return ctx.member;
}

export function HomeLineupAdminControls() {
  const ctx = useContext(HomeLineupPanelContext);
  if (!ctx) return null;
  return ctx.admin;
}

export function HomeLineupPanel(props: HomeLineupPanelProps) {
  const { t } = useTranslation();
  const { detachAdmin, ...rest } = props;
  const slots = useHomeLineupPanelSlots(rest);

  if (!slots.hasPlayers) {
    return slots.member;
  }

  if (detachAdmin) {
    return slots.member;
  }

  return (
    <div className="space-y-4">
      {slots.member}
      {slots.admin && (
        <HomeAdminZone
          title={t("v2.home.adminZoneLineupTitle")}
          description={
            !rest.hasPublishedLineup
              ? t("v2.home.lineupAdminDraftHint")
              : t("v2.home.adminZoneLineupDesc")
          }
          actionRequired={!rest.hasPublishedLineup}
        >
          {slots.admin}
        </HomeAdminZone>
      )}
    </div>
  );
}
